import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Switch } from "./Switch";
import { type SwitchDataProps, switchDataSchema } from "./Switch.schema";
import { SWITCH_TAG_NAME } from "./constants";

export interface SwitchHandle {
  checked: (value?: boolean) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [SWITCH_TAG_NAME]: { props: SwitchDataProps; handle: SwitchHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-switch": HTMLElement & SwitchHandle;
  }
}

export function RWCSwitch({
  ref,
  checked: initialChecked,
  disabled: initialDisabled,
  inputId,
  value,
  ...props
}: SwitchDataProps & { ref?: Ref<SwitchHandle> }) {
  const host = useIslandHost();

  const [checked, setChecked] = useState(initialChecked ?? false);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  const handle: SwitchHandle = {
    checked: (value = true) => setChecked(value),
    disabled: (value = true) => setDisabled(value),
  };

  useImperativeHandle(ref, () => handle, []);

  useEffect(() => {
    getElementInternals(host)?.setFormValue(checked ? (value ?? "on") : null);
    host?.toggleAttribute("checked", checked);
  }, [host, checked, value]);

  function handleCheckedChange(next: boolean) {
    setChecked(next);
    host?.toggleAttribute("checked", next);
    host?.dispatchEvent(
      new CustomEvent("switch:change", {
        detail: { checked: next },
        bubbles: true,
      }),
    );
  }

  return (
    <Switch
      {...props}
      value={value}
      id={inputId}
      checked={checked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
    />
  );
}

componentManager.register(
  rwc(SWITCH_TAG_NAME, {
    component: RWCSwitch,
    dataSchema: switchDataSchema,
    formAssociated: true,
  }),
);
